import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import userAPI from '../../api/userAPI';
import getAsyncErrorMessage from '../../utils/getAsyncErrorMessage';
import { GENERAL_ERROR_MESSAGE } from '../../constants';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk } from '../store';
import type { RejectedWithValueAction } from '../../types/common';
import type { CreateUserParams, DeleteUserParams, GetUsersParams, ModalMode, UpdateUserParams, UserState, UserWithOrganizationType } from '../../types/user';

const initialState: UserState = {
  isLoading: false,
  hasError: false,
  serverMessage: undefined,
  dialogMode: undefined,
  isDialogOpen: false,
  selectedOrganizationId: undefined,
  user: undefined,
  users: undefined,
  adminPowerUsers: undefined,
};

const slice = createSlice({
  name: 'User',
  initialState,
  reducers: {
    startLoading(state: UserState) {
      state.isLoading = true;
    },

    hasError(state: UserState, action: PayloadAction<string>) {
      const message = action.payload;
      state.isLoading = false;
      state.hasError = true;
      state.serverMessage = message;
    },

    resetMessage(state: UserState) {
      state.isLoading = false;
      state.serverMessage = undefined;
      state.hasError = false;
    },

    setServerMessage(state: UserState, action: PayloadAction<string>): void {
      state.isLoading = false;
      state.serverMessage = action.payload;
    },

    setDialogMode(state: UserState, action: PayloadAction<ModalMode>): void {
      state.dialogMode = action.payload;
    },

    openDialog(state: UserState): void {
      state.isDialogOpen = true;
    },

    closeDialog(state: UserState): void {
      state.isDialogOpen = false;
      state.dialogMode = undefined;
      state.user = undefined;
    },

    setOrganizationId(state: UserState, action: PayloadAction<number>): void {
      state.selectedOrganizationId = action.payload;
    },

    selectUser(state: UserState, action: PayloadAction<UserWithOrganizationType | undefined>): void {
      if (action.payload !== undefined) {
        const user: UserWithOrganizationType = action.payload;
        const selectedUser: UserWithOrganizationType | undefined = state.users.find((org) => org.id === user.id);
        if (selectedUser !== undefined) {
          state.user = selectedUser;
        } else {
          state.user = action.payload;
        }
      } else {
        state.user = undefined;
      }
    },
  },

  extraReducers: (builder) => {
    builder
      // Get Users
      // ----------------------------------------
      .addCase(getUsers.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = action.payload;
        // const sortedUsers: UserWithOrganizationType[] = action.payload.sort(sortUser);
        // state.users = sortedUsers;
      })
      .addCase(getUsers.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<GetUsersParams, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.users = state.users === undefined ? [] : state.users;
      })
      // Get Admin / Powerusers
      // ----------------------------------------
      .addCase(getAdminPowerUsers.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getAdminPowerUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.adminPowerUsers = action.payload;
        // const sortedAdminPowerUsers: AdminPowerUsers = {
        //   admin: action.payload.admin.sort(sortAdminPowerUsers),
        //   poweruser: action.payload.poweruser.sort(sortAdminPowerUsers),
        // };
        // state.adminPowerUsers = sortedAdminPowerUsers;
      })
      .addCase(getAdminPowerUsers.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<undefined, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.adminPowerUsers = state.adminPowerUsers === undefined ? { admin: [], poweruser: [] } : state.adminPowerUsers;
      })
      // Create User
      // ----------------------------------------
      .addCase(createUser.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hasError = false;
        // state.serverMessage = 'New user has been created.';
        const newUser: UserWithOrganizationType = action.payload;
        const newUsers: UserWithOrganizationType[] = state.users ? [...state.users, newUser] : [newUser];
        state.users = newUsers;
        // const sortedNewUsers: UserWithOrganizationType[] = newUsers.sort(sortUser);
        // state.users = sortedNewUsers;
      })
      .addCase(createUser.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<CreateUserParams, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.users = state.users ? state.users : [];
      })
      // Update User
      // ----------------------------------------
      .addCase(updateUser.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hasError = false;
        // state.serverMessage = 'User details has been updated.';
        const updatedUser: UserWithOrganizationType = action.payload;
        // console.log(updateUser);
        const newUsers: UserWithOrganizationType[] = state.users
          ? state.users.map((user) => {
              if (user.id === updatedUser.id) {
                return updatedUser;
              } else {
                return user;
              }
            })
          : [updatedUser];
        state.users = newUsers;
      })
      .addCase(updateUser.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<UpdateUserParams, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.users = state.users === undefined ? [] : state.users;
      })
      // Delete User
      // ----------------------------------------
      .addCase(deleteUser.pending, (state, action) => {
        state.isLoading = true;
        state.hasError = false;
        // state.serverMessage = 'User has been deleted.';
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = undefined;
        state.users = state.users.filter((user) => {
          if (user.id === action.payload.id) {
            return false;
          } else {
            return true;
          }
        });
      })
      .addCase(deleteUser.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<DeleteUserParams, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.user = undefined;
        state.users = state.users === undefined ? [] : state.users;
      });
  },
});

export const { reducer, actions } = slice;

export const resetMessage =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.resetMessage());
  };

export const setDialogMode =
  (mode: ModalMode): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.setDialogMode(mode));
  };

export const openDialog =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.openDialog());
  };

export const closeDialog =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.closeDialog());
  };

export const setOrganizationId =
  (organizationId?: number): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.setOrganizationId(organizationId));
  };

export const selectUser =
  (user?: UserWithOrganizationType): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.selectUser(user));
  };

export const getUsers = createAsyncThunk('ManageUser/getUsers', async (params: GetUsersParams, thunkAPI) => {
  try {
    const response = await userAPI.getUsers(params);
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const getAdminPowerUsers = createAsyncThunk('ManageUser/getAdminPowerUsers', async (_, thunkAPI) => {
  try {
    const response = await userAPI.getAdminPowerUsers();
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const createUser = createAsyncThunk('ManageUser/createUser', async (params: CreateUserParams, thunkAPI) => {
  try {
    const response = await userAPI.createUser(params);
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const updateUser = createAsyncThunk('ManageUser/updateUser', async (params: UpdateUserParams, thunkAPI) => {
  try {
    const response = await userAPI.updateUser(params);
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const deleteUser = createAsyncThunk('ManageUser/deleteUser', async (params: DeleteUserParams, thunkAPI) => {
  try {
    const response = await userAPI.deleteUser(params);
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export default slice;
